import { createContext, useState, useEffect, useContext } from 'react';
import { isAxiosError } from 'axios';
import { clearQueryCache } from '@/config/queryClient';
import {
  AppError,
  setContext,
  useAnalytics,
  useErrors,
  useLogger,
  useMetrics,
} from '@/observability';
import api from '@/services/api';
import {
  AUTH_STORAGE_KEYS,
  AUTH_UNAUTHORIZED_EVENT,
  type IAuthContextValue,
  type IAuthProviderProps,
  type ILoginResponse,
  type IUser,
  type IUsuarioMeResponse,
  type TAuthHeaders,
} from '@/types/auth';
import {
  isSuperAdmin,
  readEffectiveTenantId,
  withTenantHeader,
  writeEffectiveTenantId,
} from '@/utils/tenantScope';

const AuthContext = createContext<IAuthContextValue | null>(null);

function toStoredUser(me: IUsuarioMeResponse): IUser {
  return {
    id: me.id,
    email: me.email,
    nome: me.nome,
    role: me.role,
    tenant_id: me.tenant_id,
    unidades_visiveis: me.unidades_visiveis ?? [],
  };
}

function clearLocalAuth(): void {
  localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
  localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
  localStorage.removeItem(AUTH_STORAGE_KEYS.userData);
  localStorage.removeItem(AUTH_STORAGE_KEYS.effectiveTenantId);
}

export function AuthProvider({ children }: IAuthProviderProps) {
  const logger = useLogger();
  const errors = useErrors();
  const analytics = useAnalytics();
  const metrics = useMetrics();

  const [user, setUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [effectiveTenantId, setEffectiveTenantIdState] = useState<string | null>(() =>
    readEffectiveTenantId(),
  );

  const persistUser = (nextUser: IUser): void => {
    localStorage.setItem(AUTH_STORAGE_KEYS.userData, JSON.stringify(nextUser));
    setUser(nextUser);

    // User normal: limpa personificação residual. Super admin: mantém escolha salva.
    if (!isSuperAdmin(nextUser)) {
      writeEffectiveTenantId(null);
      setEffectiveTenantIdState(null);
    }
  };

  const setEffectiveTenantId = (tenantId: string | null): void => {
    if (!isSuperAdmin(user)) return;
    writeEffectiveTenantId(tenantId);
    setEffectiveTenantIdState(tenantId);
    clearQueryCache();
  };

  useEffect(() => {
    const checkSession = async (): Promise<void> => {
      try {
        const savedToken = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
        const savedUser = localStorage.getItem(AUTH_STORAGE_KEYS.userData);

        if (savedToken && savedUser) {
          try {
            const me = await api.get<IUsuarioMeResponse>('/auth/me');
            const nextUser = toStoredUser(me);
            persistUser(nextUser);
            setAccessToken(savedToken);
            if (isSuperAdmin(nextUser)) {
              setEffectiveTenantIdState(readEffectiveTenantId());
            }
          } catch (sessionError) {
            // 401 esperado (token expirado) — logger só, sem flood em errors.
            if (isAxiosError(sessionError) && sessionError.response?.status === 401) {
              logger.info('Session invalid', { status: 401 });
            } else {
              logger.warn('Session check failed');
            }
            clearLocalAuth();
            setUser(null);
            setAccessToken(null);
            setEffectiveTenantIdState(null);
          }
        } else {
          setUser(null);
          setAccessToken(null);
          setEffectiveTenantIdState(null);
        }
      } catch (error) {
        logger.error('Session bootstrap failed');
        errors.capture(error, { tags: { source: 'auth.session' } });
        setUser(null);
        setAccessToken(null);
        setEffectiveTenantIdState(null);
      } finally {
        setLoading(false);
      }
    };

    void checkSession();
  }, [logger, errors]);

  useEffect(() => {
    if (!user) {
      setContext({ tenantId: null, userIdHash: undefined, role: undefined });
      return;
    }
    setContext({
      // id interno (não email) — campo userIdHash
      userIdHash: user.id,
      role: user.role,
      tenantId: isSuperAdmin(user) ? (effectiveTenantId ?? user.tenant_id) : user.tenant_id,
    });
  }, [user, effectiveTenantId]);

  useEffect(() => {
    const onUnauthorized = (): void => {
      setUser(null);
      setAccessToken(null);
      setEffectiveTenantIdState(null);
    };

    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const login = async (email: string, senha: string): Promise<boolean> => {
    try {
      return await metrics.measure('auth.login', async () => {
        const data = await api.post<ILoginResponse>('/auth/login', { email, senha });

        if (!data.access_token) {
          errors.capture(
            new AppError({
              code: 'AUTH_TOKEN_MISSING',
              message: 'Token não recebido na resposta de login',
            }),
            { tags: { source: 'auth.login' } },
          );
          analytics.track('Login Failed', { reason: 'token_missing' });
          return false;
        }

        localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, data.access_token);
        localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, data.refresh_token ?? '');

        const me = await api.get<IUsuarioMeResponse>('/auth/me');
        const nextUser = toStoredUser(me);
        persistUser(nextUser);

        clearQueryCache();

        setAccessToken(data.access_token);
        if (isSuperAdmin(nextUser)) {
          setEffectiveTenantIdState(readEffectiveTenantId());
        }

        analytics.track('Login Succeeded');
        return true;
      });
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      logger.error('Login failed', status != null ? { status } : undefined);
      // Credenciais inválidas são esperadas — captura só falhas inesperadas.
      if (!isAxiosError(error) || status == null || status >= 500) {
        errors.capture(error, { tags: { source: 'auth.login' } });
      }
      analytics.track('Login Failed', status != null ? { status } : undefined);
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
    try {
      if (refreshToken) {
        await api.post('/auth/logout', { refresh_token: refreshToken });
      }
    } catch (error) {
      logger.warn('Logout revoke failed');
      errors.capture(error, { tags: { source: 'auth.logout' } });
    } finally {
      clearLocalAuth();
      clearQueryCache();
      setUser(null);
      setAccessToken(null);
      setEffectiveTenantIdState(null);
      analytics.track('Logout');
    }
  };

  const getAuthHeaders = (): TAuthHeaders => {
    const token = accessToken || localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
    const base: TAuthHeaders = {
      'Content-Type': 'application/json',
    };
    if (token) {
      base.Authorization = `Bearer ${token}`;
    }
    return withTenantHeader(base, user);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading,
        accessToken,
        effectiveTenantId: isSuperAdmin(user) ? effectiveTenantId : null,
        setEffectiveTenantId,
        getAuthHeaders,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): IAuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return context;
}
